import type {
  AnswersImportPayload,
  CatalogueItem,
  EncryptedEnvelope,
  Experience,
  Interest,
  Participant,
  ParticipantAnswer,
  PerspectiveAnswer,
  LocalSession,
  RemoteCredentials,
  RemoteRoomView,
  RoomSnapshotWire,
  Seat,
  SharedNote,
  TakeLocalRoomOnlineOptions,
} from "../types";
import { MAX_PERSONAL_CONTEXT_LENGTH } from "../types";
import type {
  ClientMessage,
  CreateRoomRequest,
  ImportRoomRequest,
  ServerMessage,
} from "../../shared/protocol";
import {
  CAPABILITY_PATTERN,
  MAX_ROOM_IMPORT_BYTES,
  ROOM_ID_PATTERN,
} from "../../shared/protocol";
import {
  decryptJson,
  encryptJson,
  hashToken,
  randomToken,
} from "./crypto";
import { CATALOGUE, CATALOGUE_VERSION } from "../data/catalogue";
import {
  DEFAULT_ITEM_ID,
  normalizeAnswerRecord,
  normalizeParticipantAnswer,
} from "./session";

const INTEREST_VALUES = new Set<string>([
  "need",
  "yearn",
  "want",
  "open",
  "dislike",
  "hard-limit",
  "curious",
]);
const EXPERIENCE_VALUES = new Set<string>([
  "expert",
  "confident",
  "familiar",
  "learning",
  "once",
  "never",
]);

class InvalidRoomPayloadError extends Error {}

export interface CreatedRemoteRoom {
  credentials: RemoteCredentials;
  ownUrl: string;
  partnerUrl: string;
  expiresAt: number;
}

export interface CreatedRemoteRoomFromLocal extends CreatedRemoteRoom {
  participants: Record<Seat, Participant>;
}

function fragmentFor(credentials: RemoteCredentials) {
  const params = new URLSearchParams({
    room: credentials.roomId,
    seat: credentials.seat,
    cap: credentials.capability,
    key: credentials.key,
  });
  return params.toString();
}

export function credentialsFromHash(hash = window.location.hash) {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const roomId = params.get("room");
  const seat = params.get("seat");
  const capability = params.get("cap");
  const key = params.get("key");
  if (
    !roomId ||
    !ROOM_ID_PATTERN.test(roomId) ||
    (seat !== "A" && seat !== "B") ||
    !capability ||
    !CAPABILITY_PATTERN.test(capability) ||
    !key ||
    !/^[A-Za-z0-9_-]{43}$/.test(key)
  ) {
    return null;
  }
  return { roomId, seat, capability, key } satisfies RemoteCredentials;
}

export async function createRemoteRoom(
  participants: Record<Seat, Participant>,
  localCopy?: {
    session: LocalSession;
    selection: TakeLocalRoomOnlineOptions["selection"];
  },
): Promise<CreatedRemoteRoom> {
  const roomId = randomToken(18);
  const capabilityA = randomToken();
  const capabilityB = randomToken();
  const key = randomToken();
  const [hashA, hashB, profileA, profileB, initialState] = await Promise.all([
    hashToken(capabilityA),
    hashToken(capabilityB),
    encryptJson(key, participants.A, `${roomId}:profile:A`),
    encryptJson(key, participants.B, `${roomId}:profile:B`),
    localCopy
      ? encryptLocalCopy(roomId, key, localCopy.session, localCopy.selection)
      : Promise.resolve(undefined),
  ]);

  const payload: CreateRoomRequest = {
    roomId,
    catalogueVersion: CATALOGUE_VERSION,
    capabilityHashes: { A: hashA, B: hashB },
    profileEnvelopes: { A: profileA, B: profileB },
    initialState,
  };

  const response = await fetch("/api/rooms", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      response.status === 429
        ? "Room creation is busy. Wait a moment and try again."
        : `The room could not be created. ${detail}`.trim(),
    );
  }
  const room = (await response.json()) as { expiresAt: number };
  if (!Number.isFinite(room.expiresAt)) {
    throw new Error("The room was created without a valid expiry time.");
  }

  const own: RemoteCredentials = {
    roomId,
    seat: "A",
    capability: capabilityA,
    key,
  };
  const partner: RemoteCredentials = {
    roomId,
    seat: "B",
    capability: capabilityB,
    key,
  };
  const base = `${window.location.origin}${window.location.pathname}`;
  return {
    credentials: own,
    ownUrl: `${base}#${fragmentFor(own)}`,
    partnerUrl: `${base}#${fragmentFor(partner)}`,
    expiresAt: room.expiresAt,
  };
}

export async function createRemoteRoomFromLocal(
  session: LocalSession,
  options: TakeLocalRoomOnlineOptions,
): Promise<CreatedRemoteRoomFromLocal> {
  const selectedParticipant =
    options.selection === "B"
      ? session.participants.B
      : session.participants.A;
  const participants: Record<Seat, Participant> =
    options.selection === "both"
      ? session.participants
      : {
          A: selectedParticipant,
          B: {
            ...options.newPartner,
            name: options.newPartner.name.trim(),
          },
        };
  const created = await createRemoteRoom(participants, {
    session,
    selection: options.selection,
  });
  return { ...created, participants };
}

async function encryptLocalCopy(
  roomId: string,
  key: string,
  session: LocalSession,
  selection: TakeLocalRoomOnlineOptions["selection"],
): Promise<NonNullable<CreateRoomRequest["initialState"]>> {
  const copiedAt = Date.now();
  const seatMappings: Array<{ source: Seat; target: Seat }> =
    selection === "both"
      ? [
          { source: "A", target: "A" },
          { source: "B", target: "B" },
        ]
      : [{ source: selection, target: "A" }];
  const answerSources = seatMappings.flatMap(({ source, target }) =>
    Object.values(session.answers[source])
      .filter((answer) => answer.savedAt !== null)
      .map((answer) => ({ source, target, answer })),
  );
  const copiedAnswerIds = new Set(
    answerSources.map(({ answer }) => answer.itemId),
  );
  const customSources = session.customEntries.filter(
    (entry) =>
      selection === "both" ||
      entry.createdBy === selection ||
      copiedAnswerIds.has(entry.id),
  );

  const [answers, notes, customEntries] = await Promise.all([
    Promise.all(
      answerSources.map(async ({ target, answer }) => {
        const payload: ParticipantAnswer = {
          ...answer,
          savedAt: copiedAt,
          updatedAt: copiedAt,
          version: 1,
          changedAfterReveal: false,
        };
        return {
          seat: target,
          itemId: answer.itemId,
          envelope: await encryptJson(
            key,
            payload,
            `${roomId}:answer:${target}:${answer.itemId}`,
          ),
        };
      }),
    ),
    selection === "both"
      ? Promise.all(
          Object.values(session.notes).map(async (note) => ({
            itemId: note.itemId,
            envelope: await encryptJson(
              key,
              { ...note, version: 1, updatedAt: copiedAt },
              `${roomId}:note:${note.itemId}`,
            ),
          })),
        )
      : Promise.resolve([]),
    Promise.all(
      customSources.map(async (entry) => {
        const createdBy: Seat =
          selection === "both" ? entry.createdBy ?? "A" : "A";
        const locked = Boolean(entry.locked || copiedAnswerIds.has(entry.id));
        const payload: CatalogueItem = {
          ...entry,
          createdBy,
          locked,
          customVersion: 1,
        };
        return {
          entryId: entry.id,
          envelope: await encryptJson(
            key,
            payload,
            `${roomId}:custom:${entry.id}`,
          ),
          createdBy,
          locked,
        };
      }),
    ),
  ]);

  return { answers, notes, customEntries };
}

export class RemoteRoomConnection extends EventTarget {
  readonly credentials: RemoteCredentials;
  private socket: WebSocket | null = null;
  private reconnectTimer = 0;
  private stopped = false;
  private reconnectAttempt = 0;

  constructor(credentials: RemoteCredentials) {
    super();
    this.credentials = credentials;
  }

  async connect() {
    this.stopped = false;
    try {
      const status = await fetch(`/api/rooms/${this.credentials.roomId}`, {
        headers: {
          authorization: `Bearer ${this.credentials.capability}`,
          "x-feel-scene-seat": this.credentials.seat,
        },
      });
      if (status.status === 410) {
        this.dispatchEvent(
          new CustomEvent("terminal", { detail: "expired" }),
        );
        this.stopped = true;
        return;
      }
      if (!status.ok) {
        throw new Error(
          status.status === 401
            ? "This invite link is incomplete or no longer valid."
            : "The room could not be reached.",
        );
      }
    } catch (error) {
      if (this.stopped) return;
      this.dispatchEvent(
        new CustomEvent("error", {
          detail:
            error instanceof Error
              ? error.message
              : "The room could not be reached.",
        }),
      );
      this.scheduleReconnect();
      return;
    }
    const scheme = location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${scheme}//${location.host}/api/rooms/${this.credentials.roomId}/socket`;
    this.socket = new WebSocket(url, [
      "feel-scene-v1",
      this.credentials.seat,
      this.credentials.capability,
    ]);
    this.socket.addEventListener("open", () => {
      this.reconnectAttempt = 0;
      this.dispatchEvent(new CustomEvent("status", { detail: true }));
    });
    this.socket.addEventListener("message", (event) => {
      void this.handleMessage(String(event.data));
    });
    this.socket.addEventListener("close", (event) => {
      this.dispatchEvent(new CustomEvent("status", { detail: false }));
      if (event.code === 4001 || event.code === 4002) {
        const state = event.code === 4001 ? "expired" : "deleted";
        this.dispatchEvent(new CustomEvent("terminal", { detail: state }));
        this.stopped = true;
        return;
      }
      if (!this.stopped) this.scheduleReconnect();
    });
  }

  disconnect() {
    this.stopped = true;
    clearTimeout(this.reconnectTimer);
    this.socket?.close(1000, "Client left");
  }

  send(message: ClientMessage) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      throw new Error("You are offline. Your draft is preserved; reconnect to save.");
    }
    this.socket.send(JSON.stringify(message));
  }

  private scheduleReconnect() {
    const delay = Math.min(10_000, 500 * 2 ** this.reconnectAttempt);
    this.reconnectAttempt += 1;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = window.setTimeout(() => void this.connect(), delay);
  }

  private async handleMessage(raw: string) {
    let message: ServerMessage;
    try {
      message = JSON.parse(raw) as ServerMessage;
    } catch {
      return;
    }
    if (message.type === "snapshot") {
      try {
        const view = await decryptSnapshot(message, this.credentials);
        this.dispatchEvent(new CustomEvent("snapshot", { detail: view }));
      } catch (error) {
        this.dispatchEvent(
          new CustomEvent("error", {
            detail:
              error instanceof InvalidRoomPayloadError
                ? "This room rejected an invalid encrypted update. Reload, or start a new room if it continues."
                : "This room link cannot decrypt the room. Use the complete original invite link.",
          }),
        );
      }
      return;
    }
    this.dispatchEvent(new CustomEvent(message.type, { detail: message }));
  }
}

async function decryptAnswerRows(
  rows: RoomSnapshotWire["ownAnswers"],
  credentials: RemoteCredentials,
  seat: Seat,
) {
  const answers: Record<string, ParticipantAnswer> = {};
  await Promise.all(
    rows.map(async (row) => {
      const answer = await decryptJson<unknown>(
        credentials.key,
        row.envelope,
        `${credentials.roomId}:answer:${seat}:${row.itemId}`,
      );
      answers[row.itemId] = validParticipantAnswer(answer, row);
    }),
  );
  return answers;
}

export async function decryptSnapshot(
  snapshot: RoomSnapshotWire,
  credentials: RemoteCredentials,
): Promise<RemoteRoomView> {
  const partner: Seat = credentials.seat === "A" ? "B" : "A";
  const [profileA, profileB, ownAnswers, partnerAnswers, notes, customEntries] =
    await Promise.all([
      decryptProfile(snapshot.profileEnvelopes.A, credentials, "A"),
      decryptProfile(snapshot.profileEnvelopes.B, credentials, "B"),
      decryptAnswerRows(snapshot.ownAnswers, credentials, credentials.seat),
      decryptAnswerRows(snapshot.partnerAnswers, credentials, partner),
      decryptNotes(snapshot.notes, credentials),
      decryptCustomEntries(snapshot.customEntries, credentials),
    ]);
  const items = [...CATALOGUE, ...customEntries];
  const ownParticipant = credentials.seat === "A" ? profileA : profileB;
  const partnerParticipant = credentials.seat === "A" ? profileB : profileA;

  return {
    mode: "remote",
    roomId: snapshot.roomId,
    catalogueVersion: snapshot.catalogueVersion,
    createdAt: snapshot.createdAt,
    expiresAt: snapshot.expiresAt,
    participants: { A: profileA, B: profileB },
    profileVersions: {
      A: snapshot.profileVersions.A ?? 0,
      B: snapshot.profileVersions.B ?? 0,
    },
    seat: credentials.seat,
    ownAnswers: normalizeAnswerRecord(
      ownAnswers,
      items,
      ownParticipant.stance,
    ),
    partnerAnswers: normalizeAnswerRecord(
      partnerAnswers,
      items,
      partnerParticipant.stance,
    ),
    partnerSaved: snapshot.partnerSaved,
    notes,
    customEntries,
    activeItemId: DEFAULT_ITEM_ID,
    partnerActiveItemId: snapshot.partnerActiveItemId,
    connected: true,
    terminalState: null,
  };
}

async function decryptProfile(
  envelope: EncryptedEnvelope | undefined,
  credentials: RemoteCredentials,
  seat: Seat,
) {
  if (!envelope) {
    return {
      name: seat === "A" ? "Participant A" : "Participant B",
      stance: "switch",
    } satisfies Participant;
  }
  return validParticipant(
    await decryptJson<unknown>(
      credentials.key,
      envelope,
      `${credentials.roomId}:profile:${seat}`,
    ),
  );
}

async function decryptNotes(
  rows: RoomSnapshotWire["notes"],
  credentials: RemoteCredentials,
) {
  const notes: Record<string, SharedNote> = {};
  await Promise.all(
    rows.map(async (row) => {
      const note = await decryptJson<unknown>(
        credentials.key,
        row.envelope,
        `${credentials.roomId}:note:${row.itemId}`,
      );
      notes[row.itemId] = validSharedNote(note, row);
    }),
  );
  return notes;
}

async function decryptCustomEntries(
  rows: RoomSnapshotWire["customEntries"],
  credentials: RemoteCredentials,
) {
  return Promise.all(
    rows.map(async (row) =>
      validCustomItem(
        await decryptJson<unknown>(
          credentials.key,
          row.envelope,
          `${credentials.roomId}:custom:${row.entryId}`,
        ),
        row,
      ),
    ),
  );
}

function validParticipant(value: unknown): Participant {
  const candidate = objectValue(value);
  if (
    typeof candidate.name !== "string" ||
    candidate.name.length === 0 ||
    candidate.name.length > 40 ||
    !["dominant", "submissive", "switch"].includes(String(candidate.stance))
  ) {
    invalidRoomPayload();
  }
  return {
    name: candidate.name,
    stance: candidate.stance as Participant["stance"],
  };
}

function validParticipantAnswer(
  value: unknown,
  row: RoomSnapshotWire["ownAnswers"][number],
): ParticipantAnswer {
  const candidate = objectValue(value);
  const perspectiveSource = objectValue(candidate.perspectives);
  const perspectiveEntries = Object.entries(perspectiveSource);
  if (
    candidate.itemId !== row.itemId ||
    (candidate.responseScaleVersion !== undefined &&
      candidate.responseScaleVersion !== 2) ||
    typeof candidate.meaning !== "string" ||
    candidate.meaning.length > 2_000 ||
    typeof candidate.context !== "string" ||
    candidate.context.length > MAX_PERSONAL_CONTEXT_LENGTH ||
    typeof candidate.important !== "boolean" ||
    typeof candidate.updatedAt !== "number" ||
    !Number.isFinite(candidate.updatedAt) ||
    perspectiveEntries.length < 1 ||
    perspectiveEntries.length > 2
  ) {
    invalidRoomPayload();
  }
  const perspectives = Object.create(
    null,
  ) as ParticipantAnswer["perspectives"];
  for (const [label, rawPerspective] of perspectiveEntries) {
    const perspective = objectValue(rawPerspective);
    if (
      label.length === 0 ||
      label.length > 80 ||
      typeof perspective.enabled !== "boolean" ||
      (perspective.interest !== null &&
        !INTEREST_VALUES.has(String(perspective.interest))) ||
      (perspective.experience !== null &&
        !EXPERIENCE_VALUES.has(String(perspective.experience)))
    ) {
      invalidRoomPayload();
    }
    perspectives[label] = {
      enabled: perspective.enabled,
      interest: perspective.interest as Interest | "curious" | null,
      experience: perspective.experience as Experience | null,
    } as PerspectiveAnswer;
  }
  return normalizeParticipantAnswer({
    itemId: row.itemId,
    perspectives,
    responseScaleVersion:
      candidate.responseScaleVersion === 2 ? 2 : undefined,
    meaning: candidate.meaning,
    context: candidate.context,
    important: candidate.important,
    savedAt: row.savedAt,
    updatedAt: candidate.updatedAt,
    version: row.version,
    changedAfterReveal: row.changedAfterReveal,
  });
}

function validSharedNote(
  value: unknown,
  row: RoomSnapshotWire["notes"][number],
): SharedNote {
  const candidate = objectValue(value);
  if (
    candidate.itemId !== row.itemId ||
    typeof candidate.text !== "string" ||
    candidate.text.length > 2_000 ||
    typeof candidate.important !== "boolean" ||
    typeof candidate.updatedAt !== "number" ||
    !Number.isFinite(candidate.updatedAt)
  ) {
    invalidRoomPayload();
  }
  return {
    itemId: row.itemId,
    text: candidate.text,
    important: candidate.important,
    updatedAt: candidate.updatedAt,
    version: row.version,
  };
}

function validCustomItem(
  value: unknown,
  row: RoomSnapshotWire["customEntries"][number],
): CatalogueItem {
  const candidate = objectValue(value);
  const framing = String(candidate.framing);
  const perspectives = Array.isArray(candidate.perspectives)
    ? candidate.perspectives
    : [];
  const expectedPerspectiveCount = framing === "shared" ? 1 : 2;
  if (
    candidate.id !== row.entryId ||
    typeof candidate.term !== "string" ||
    candidate.term.trim().length === 0 ||
    candidate.term.length > 100 ||
    typeof candidate.definition !== "string" ||
    candidate.definition.length > 500 ||
    !["shared", "directional", "role-paired"].includes(framing) ||
    !["both", "dominant-first", "dominant-second"].includes(
      String(candidate.stanceDefault),
    ) ||
    perspectives.length !== expectedPerspectiveCount ||
    perspectives.some(
      (label) =>
        typeof label !== "string" || label.length === 0 || label.length > 80,
    ) ||
    new Set(perspectives).size !== perspectives.length ||
    (row.createdBy !== "A" && row.createdBy !== "B")
  ) {
    invalidRoomPayload();
  }
  return {
    id: row.entryId,
    term: candidate.term,
    definition: candidate.definition,
    aliases: [],
    framing: framing as CatalogueItem["framing"],
    stanceDefault: candidate.stanceDefault as CatalogueItem["stanceDefault"],
    perspectives: perspectives as string[],
    categoryId: "custom",
    categoryName: "Custom terms",
    subgroupId: null,
    subgroupName: null,
    custom: true,
    createdBy: row.createdBy,
    locked: row.locked,
    customVersion: row.version,
  };
}

function objectValue(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    invalidRoomPayload();
  }
  return value as Record<string, unknown>;
}

function invalidRoomPayload(): never {
  throw new InvalidRoomPayloadError("The encrypted room payload is invalid.");
}

export async function encryptedAnswerMessage(
  credentials: RemoteCredentials,
  answer: ParticipantAnswer,
): Promise<ClientMessage> {
  const now = Date.now();
  const payload: ParticipantAnswer = {
    ...answer,
    savedAt: now,
    updatedAt: now,
    version: answer.version + 1,
  };
  return {
    type: "answer:save",
    itemId: answer.itemId,
    version: answer.version,
    envelope: await encryptJson(
      credentials.key,
      payload,
      `${credentials.roomId}:answer:${credentials.seat}:${answer.itemId}`,
    ),
  };
}

export async function importRemoteAnswers(
  credentials: RemoteCredentials,
  payload: AnswersImportPayload,
): Promise<void> {
  const [answerMessages, customMessages] = await Promise.all([
    Promise.all(
      payload.answers.map((answer) =>
        encryptedAnswerMessage(credentials, answer),
      ),
    ),
    Promise.all(
      payload.customEntries.map((item) =>
        encryptedCustomMessage(credentials, item, "custom:create"),
      ),
    ),
  ]);
  const customs = customMessages.map((message) => {
    if (message.type !== "custom:create") {
      throw new Error("The custom-item import could not be prepared.");
    }
    return { entryId: message.entryId, envelope: message.envelope };
  });
  const answers = answerMessages.map((message) => {
    if (message.type !== "answer:save") {
      throw new Error("The answers import could not be prepared.");
    }
    return {
      itemId: message.itemId,
      envelope: message.envelope,
      version: message.version,
    };
  });
  const requestPayload: ImportRoomRequest = {
    customEntries: customs,
    answers,
  };
  const body = JSON.stringify(requestPayload);
  if (new TextEncoder().encode(body).byteLength > MAX_ROOM_IMPORT_BYTES) {
    throw new Error("The encrypted answers are too large for an online room.");
  }
  const importId = randomToken(18);
  const request = () =>
    fetch(`/api/rooms/${credentials.roomId}/import`, {
      method: "POST",
      headers: {
        authorization: `Bearer ${credentials.capability}`,
        "content-type": "application/json",
        "x-feel-scene-import-id": importId,
        "x-feel-scene-seat": credentials.seat,
      },
      body,
    });
  let response: Response;
  try {
    response = await request();
  } catch {
    response = await request();
  }
  if (response.status >= 500) response = await request();
  if (!response.ok) {
    const detail = (await response.json().catch(() => null)) as {
      error?: string;
    } | null;
    throw new Error(
      detail?.error ??
        (response.status === 429
          ? "Wait one minute before importing answers again."
          : "The room could not import your answers."),
    );
  }
}

export async function encryptedNoteMessage(
  credentials: RemoteCredentials,
  note: SharedNote,
): Promise<ClientMessage> {
  const payload: SharedNote = {
    ...note,
    updatedAt: Date.now(),
    version: note.version + 1,
  };
  return {
    type: "note:save",
    itemId: note.itemId,
    version: note.version,
    envelope: await encryptJson(
      credentials.key,
      payload,
      `${credentials.roomId}:note:${note.itemId}`,
    ),
  };
}

export async function encryptedProfileMessage(
  credentials: RemoteCredentials,
  profile: Participant,
  version: number,
): Promise<ClientMessage> {
  return {
    type: "profile:save",
    version,
    envelope: await encryptJson(
      credentials.key,
      profile,
      `${credentials.roomId}:profile:${credentials.seat}`,
    ),
  };
}

export async function encryptedCustomMessage(
  credentials: RemoteCredentials,
  item: CatalogueItem,
  action: "custom:create" | "custom:update",
  version = 0,
): Promise<ClientMessage> {
  const envelope = await encryptJson(
    credentials.key,
    item,
    `${credentials.roomId}:custom:${item.id}`,
  );
  return action === "custom:create"
    ? { type: action, entryId: item.id, envelope }
    : { type: action, entryId: item.id, envelope, version };
}
